import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { getCurrentUser, getAccessToken } from '../auth/authService';

/**
 * Enhanced authentication context that includes Microsoft 365 group validation
 * This extends basic MSAL authentication with backend group authorization and role management
 */
interface AuthContextType {
  // Basic authentication state
  isAuthenticated: boolean;
  isMsalInitialized: boolean;
  
  // Group authorization state
  isAuthorized: boolean | null; // null = checking, true/false = result
  isCheckingAuthorization: boolean;
  authError: string | null;
  
  // User information
  userPrincipalName: string | null;
  userGroups: string[];
  userRole: string | null; // 'Administrator' | 'User' | null
  
  // Actions
  checkGroupAuthorization: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Authentication provider that manages both MSAL authentication and group authorization
 * Implements Phase C of the M365 Group Authentication Authorization feature
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const isAuthenticated = useIsAuthenticated();
  const { instance: msalInstance, inProgress } = useMsal();
  
  // Group authorization state
  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null);
  const [isCheckingAuthorization, setIsCheckingAuthorization] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  
  // User information
  const [userPrincipalName, setUserPrincipalName] = useState<string | null>(null);
  const [userGroups, setUserGroups] = useState<string[]>([]);
  const [userRole, setUserRole] = useState<string | null>(null);
  
  // MSAL initialization state
  const isMsalInitialized = inProgress !== "startup" && inProgress !== "handleRedirect";

  /**
   * Check group authorization with backend API
   * This calls our backend group validation service
   */
  const checkGroupAuthorization = async (): Promise<void> => {
    if (!isAuthenticated || !isMsalInitialized) {
      return;
    }

    setIsCheckingAuthorization(true);
    setAuthError(null);

    try {
      // Get current user info
      const currentUser = await getCurrentUser();
      if (!currentUser) {
        throw new Error('No authenticated user found');
      }

      setUserPrincipalName(currentUser.username || currentUser.name || null);

      // Get access token for API calls
      const token = await getAccessToken(['https://graph.microsoft.com/.default']);
      
      // Call our backend group validation endpoint - use same logic as apiClient
      let baseUrl = import.meta.env.VITE_API_BASE_URL;
      
      if (!baseUrl && typeof window !== 'undefined') {
        const hostname = window.location.hostname;
        
        if (hostname === 'localhost' || hostname === '127.0.0.1') {
          baseUrl = 'http://localhost:7071/api';
        } else {
          // Custom domain - use relative /api path
          baseUrl = '/api';
        }
      } else if (!baseUrl) {
        baseUrl = 'http://localhost:7071/api';
      }
      
      console.log(`[AuthContext] Calling user authorization validation at: ${baseUrl}/auth/validate-user`);
      const response = await fetch(`${baseUrl}/auth/validate-user`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.log(`[AuthContext] Group authorization failed: ${response.status} ${errorText}`);
        throw new Error(`Group authorization check failed: ${response.status} ${errorText}`);
      }

      const result = await response.json();
      console.log(`[AuthContext] Group authorization response:`, result);

      // Parse the response structure from backend
      const authResult = result.authenticationResult || result;

      // Update state based on backend response
      setIsAuthorized(authResult.isAuthorized || false);
      setUserGroups(authResult.groups || []);
      setUserRole(authResult.userRole || null);
      
      if (authResult.userPrincipalName) {
        setUserPrincipalName(authResult.userPrincipalName);
      }

      if (!authResult.isAuthorized) {
        setAuthError(authResult.error || 'Access denied: You are not authorized to access this application');
      }

    } catch (error) {
      console.error('[AuthContext] Group authorization check failed:', error);
      setAuthError(error instanceof Error ? error.message : 'Authorization check failed');
      setIsAuthorized(false);
    } finally {
      setIsCheckingAuthorization(false);
    }
  };

  /**
   * Sign out from both MSAL and clear local state
   */
  const signOut = async (): Promise<void> => {
    try {
      // Clear local state first
      setIsAuthorized(null);
      setUserPrincipalName(null);
      setUserGroups([]);
      setUserRole(null);
      setAuthError(null);
      
      // Sign out from MSAL
      await msalInstance.logoutRedirect();
    } catch (error) {
      console.error('[AuthContext] Sign out failed:', error);
    }
  };

  /**
   * Effect to check group authorization when authentication state changes
   */
  useEffect(() => {
    if (isAuthenticated && isMsalInitialized && isAuthorized === null) {
      checkGroupAuthorization();
    } else if (!isAuthenticated) {
      // Clear state when user signs out
      setIsAuthorized(null);
      setUserPrincipalName(null);
      setUserGroups([]);
      setUserRole(null);
      setAuthError(null);
    }
  }, [isAuthenticated, isMsalInitialized]);

  const contextValue: AuthContextType = {
    // Basic authentication
    isAuthenticated,
    isMsalInitialized,
    
    // Group authorization
    isAuthorized,
    isCheckingAuthorization,
    authError,
    
    // User information
    userPrincipalName,
    userGroups,
    userRole,
    
    // Actions
    checkGroupAuthorization,
    signOut
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Hook to use the authentication context
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
